/* ============================================================
   Collection Template v2 controller (c-main-collection-v2)

   Self-contained: filtering/sorting/pagination re-render the
   section via the Section Rendering API (?section_id=...) and
   swap #CollectionV2AjaxContent. Intentionally NOT registered
   as an Empire "collection-template" section — theme.Collection
   keeps driving main-search untouched.

   Reused from the theme (loaded before this file):
   - theme.PriceRange + noUiSlider (price slider)
   - theme.routes.cartAdd + the "ajaxProduct:added" event, which
     the Empire cart drawer listens to and opens itself.
   ============================================================ */

(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';

  function CollectionV2(root) {
    if (root.dataset.cv2Init === 'true') return;
    root.dataset.cv2Init = 'true';

    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.basePath = window.location.pathname;
    this.sortValue = new URLSearchParams(window.location.search).get('sort_by') || '';
    // Keep an alternate template (?view=v2) active across AJAX renders
    this.viewParam = new URLSearchParams(window.location.search).get('view') || '';
    this.groupStates = {};
    this.adding = false;
    this.abortController = null;

    this.onPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this.onPopState);

    this.bindDelegatedEvents();
    this.initPriceRange();
  }

  CollectionV2.prototype = {
    /* ---------------- events ---------------- */

    bindDelegatedEvents: function () {
      this.root.addEventListener('click', this.onClick.bind(this));
      this.root.addEventListener('change', this.onChange.bind(this));

      // The filter form has no submit button; typing Enter in a price
      // input must apply the filter, not reload the page
      this.root.addEventListener('submit', function (evt) {
        if (evt.target.matches('[data-cv2-filter-form]')) {
          evt.preventDefault();
          this.applyFilters();
        }
      }.bind(this));

      document.addEventListener('click', function (evt) {
        var sort = this.root.querySelector('[data-cv2-sort]');
        if (sort && !sort.contains(evt.target)) {
          this.closeSortMenu();
        }
      }.bind(this));

      document.addEventListener('keyup', function (evt) {
        if (evt.key === 'Escape') {
          this.closeSortMenu();
          this.closeDrawer();
        }
      }.bind(this));
    },

    onClick: function (evt) {
      var target;

      target = evt.target.closest('[data-cv2-group-toggle]');
      if (target) {
        this.toggleGroup(target);
        return;
      }

      target = evt.target.closest('[data-cv2-filters-toggle]');
      if (target) {
        if (window.matchMedia(MOBILE_QUERY).matches) {
          this.openDrawer();
        } else {
          this.toggleSidebar(target);
        }
        return;
      }

      target = evt.target.closest('[data-cv2-drawer-close]');
      if (target) {
        this.closeDrawer();
        return;
      }

      if (evt.target.closest('[data-cv2-backdrop]')) {
        this.closeDrawer();
        return;
      }

      target = evt.target.closest('[data-cv2-sort-toggle]');
      if (target) {
        this.toggleSortMenu(target);
        return;
      }

      target = evt.target.closest('[data-cv2-sort-option]');
      if (target) {
        this.sortValue = target.dataset.value;
        this.closeSortMenu();
        this.applyFilters();
        return;
      }

      target = evt.target.closest('[data-cv2-chip], [data-cv2-reset]');
      if (target) {
        evt.preventDefault();
        this.renderFromUrl(target.href);
        return;
      }

      target = evt.target.closest('[data-cv2-pagination] a');
      if (target) {
        evt.preventDefault();
        this.renderFromUrl(target.href, { scroll: true, keepPage: true });
        return;
      }

      target = evt.target.closest('[data-cv2-swatch]');
      if (target) {
        this.selectSwatch(target);
        return;
      }

      target = evt.target.closest('[data-cv2-atc]');
      if (target) {
        this.quickAdd(target);
        return;
      }
    },

    onChange: function (evt) {
      if (evt.target.matches('[data-cv2-filter-form] input[type="checkbox"]')) {
        this.applyFilters();
        return;
      }

      if (evt.target.matches('.price-range__input')) {
        this.commitPriceInputs(evt.target.closest('.price-range'));
      }
    },

    onPopState: function () {
      this.basePath = window.location.pathname;
      this.sortValue = new URLSearchParams(window.location.search).get('sort_by') || '';
      this.render(new URLSearchParams(window.location.search), { updateHistory: false });
    },

    /* ---------------- filters / params ---------------- */

    getFilterParams: function () {
      var params = new URLSearchParams();
      var form = this.root.querySelector('[data-cv2-filter-form]');

      if (form) {
        var priceBounds = {};
        form.querySelectorAll('.price-range').forEach(function (el) {
          // data-min can be an empty string (Liquid renders no value at range 0)
          var boundMin = parseFloat(el.dataset.min);
          var boundMax = parseFloat(el.dataset.max);
          if (el.dataset.minName) {
            priceBounds[el.dataset.minName] = { bound: isNaN(boundMin) ? 0 : boundMin, type: 'min' };
          }
          if (el.dataset.maxName) {
            priceBounds[el.dataset.maxName] = { bound: isNaN(boundMax) ? Infinity : boundMax, type: 'max' };
          }
        });

        new FormData(form).forEach(function (value, key) {
          if (value === '') return;

          var priceBound = priceBounds[key];
          if (priceBound) {
            var num = parseFloat(value);
            if (isNaN(num)) return;
            // Skip price params stuck at the full range — keeps URLs clean
            if (priceBound.type === 'min' && num <= priceBound.bound) return;
            if (priceBound.type === 'max' && num >= priceBound.bound) return;
          }

          params.append(key, value);
        });
      }

      if (this.sortValue) {
        params.set('sort_by', this.sortValue);
      }

      return params;
    },

    applyFilters: function () {
      this.render(this.getFilterParams());
    },

    renderFromUrl: function (href, options) {
      options = options || {};
      var url = new URL(href, window.location.origin);
      var params = url.searchParams;

      // Only pagination links keep their page param
      if (!options.keepPage) {
        params.delete('page');
      }

      this.sortValue = params.get('sort_by') || '';
      this.render(params, options);
    },

    /* ---------------- section rendering ---------------- */

    render: function (params, options) {
      options = options || {};

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      if (this.viewParam && !params.has('view')) {
        params.set('view', this.viewParam);
      }

      var paramString = params.toString();
      var fetchUrl = this.basePath + '?section_id=' + this.sectionId + (paramString ? '&' + paramString : '');
      var displayUrl = this.basePath + (paramString ? '?' + paramString : '');

      this.root.classList.add('is-loading');

      fetch(fetchUrl, { signal: this.abortController.signal })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newContent = doc.querySelector('#CollectionV2AjaxContent');
          var currentContent = this.root.querySelector('#CollectionV2AjaxContent');

          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
          }

          if (options.updateHistory !== false) {
            window.history.pushState({ cv2: true }, '', displayUrl);
          }

          this.restoreGroupStates();
          this.initPriceRange();
          this.initOkendoWidgets();
          this.root.classList.remove('is-loading');

          if (options.scroll) {
            var toolbar = this.root.querySelector('.c-cv2__toolbar');
            if (toolbar) {
              toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }.bind(this))
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          this.root.classList.remove('is-loading');
          // Network failure — fall back to a full page load
          window.location.href = displayUrl;
        }.bind(this));
    },

    /* ---------------- price range ---------------- */

    initPriceRange: function () {
      if (!window.theme || !window.theme.PriceRange || !window.noUiSlider) return;

      this.root.querySelectorAll('.price-range').forEach(function (el) {
        var inputMin = el.querySelector('.price-range__input-min');
        var inputMax = el.querySelector('.price-range__input-max');

        new theme.PriceRange(el, {
          step: 1, // Empire's default step of 10 snaps typed values (15 -> 10)
          onChange: function () {
            this.applyFilters();
          }.bind(this),
          // Live feedback: mirror handle positions into the $ inputs while
          // dragging. Always two decimals — matches the server-rendered
          // money format so values never flicker between 6.00 and 6.
          onUpdate: function (values) {
            if (document.activeElement === inputMin || document.activeElement === inputMax) return;
            if (inputMin) inputMin.value = parseFloat(values[0]).toFixed(2);
            if (inputMax) inputMax.value = parseFloat(values[1]).toFixed(2);
          },
        });
      }.bind(this));
    },

    // Typed min/max values: clamp to the range, sync the slider, apply
    commitPriceInputs: function (container) {
      if (!container) return;

      var inputMin = container.querySelector('.price-range__input-min');
      var inputMax = container.querySelector('.price-range__input-max');
      var boundMin = parseFloat(container.dataset.min) || 0;
      var boundMax = parseFloat(container.dataset.max) || 100;

      var min = parseFloat(inputMin.value);
      var max = parseFloat(inputMax.value);
      if (isNaN(min)) min = boundMin;
      if (isNaN(max)) max = boundMax;

      min = Math.min(Math.max(min, boundMin), boundMax);
      max = Math.min(Math.max(max, min), boundMax);

      inputMin.value = min.toFixed(2);
      inputMax.value = max.toFixed(2);

      var sliderEl = container.querySelector('.price-range__slider');
      if (sliderEl && sliderEl.noUiSlider) {
        sliderEl.noUiSlider.set([min, max]);
      }

      this.applyFilters();
    },

    /* ---------------- Okendo widgets ---------------- */

    // Star ratings arriving via AJAX are not seen by Okendo's initial
    // scan — hydrate them through their widget API when available
    initOkendoWidgets: function () {
      if (!window.okeWidgetApi || typeof window.okeWidgetApi.initWidget !== 'function') return;

      this.root.querySelectorAll('[data-oke-star-rating]').forEach(function (el) {
        if (el.dataset.okeInitialized === 'true' || el.childElementCount > 0) return;
        try {
          window.okeWidgetApi.initWidget(el);
          el.dataset.okeInitialized = 'true';
        } catch (e) { /* widget API not ready — Okendo may hydrate on its own */ }
      });
    },

    /* ---------------- collapsible groups ---------------- */

    toggleGroup: function (toggleBtn) {
      var group = toggleBtn.closest('[data-cv2-group]');
      if (!group) return;

      var isClosed = group.classList.toggle('is-closed');
      toggleBtn.setAttribute('aria-expanded', isClosed ? 'false' : 'true');
      this.groupStates[group.dataset.cv2Group] = !isClosed;
    },

    restoreGroupStates: function () {
      var states = this.groupStates;
      this.root.querySelectorAll('[data-cv2-group]').forEach(function (group) {
        var saved = states[group.dataset.cv2Group];
        if (typeof saved === 'undefined') return;
        group.classList.toggle('is-closed', !saved);
        var btn = group.querySelector('[data-cv2-group-toggle]');
        if (btn) {
          btn.setAttribute('aria-expanded', saved ? 'true' : 'false');
        }
      });
    },

    /* ---------------- sidebar / drawer ---------------- */

    toggleSidebar: function (toggleBtn) {
      var hidden = this.root.classList.toggle('is-sidebar-hidden');
      toggleBtn.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      try {
        sessionStorage.setItem('cv2SidebarHidden', hidden ? 'true' : 'false');
      } catch (e) { /* storage unavailable */ }
    },

    openDrawer: function () {
      this.root.classList.add('is-drawer-open');
      document.documentElement.classList.add('cv2-scroll-lock');
    },

    closeDrawer: function () {
      this.root.classList.remove('is-drawer-open');
      document.documentElement.classList.remove('cv2-scroll-lock');
    },

    /* ---------------- sort ---------------- */

    toggleSortMenu: function (toggleBtn) {
      var menu = this.root.querySelector('[data-cv2-sort-menu]');
      if (!menu) return;
      var willOpen = menu.hidden;
      menu.hidden = !willOpen;
      toggleBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    },

    closeSortMenu: function () {
      var menu = this.root.querySelector('[data-cv2-sort-menu]');
      var toggleBtn = this.root.querySelector('[data-cv2-sort-toggle]');
      if (menu) menu.hidden = true;
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    },

    /* ---------------- swatches / quick add ---------------- */

    selectSwatch: function (swatch) {
      var card = swatch.closest('[data-cv2-card]');
      if (!card) return;

      card.querySelectorAll('[data-cv2-swatch]').forEach(function (el) {
        el.classList.remove('is-selected');
      });
      swatch.classList.add('is-selected');

      var variantId = swatch.dataset.variantId;
      card.dataset.variantId = variantId;

      var img = card.querySelector('.c-cv2-card__img--main');
      if (img && swatch.dataset.variantImage) {
        img.src = swatch.dataset.variantImage;
        img.removeAttribute('srcset');
      }

      var atcBtn = card.querySelector('[data-cv2-atc]');
      if (atcBtn) {
        atcBtn.dataset.variantId = variantId;
        var available = swatch.dataset.variantAvailable !== 'false';
        atcBtn.disabled = !available;
        atcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    },

    quickAdd: function (btn) {
      if (this.adding) return;

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      this.adding = true;
      btn.classList.add('is-loading');

      var route = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

      fetch(route, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          this.adding = false;
          btn.classList.remove('is-loading');

          if (data.status === 422 || data.status === 'bad_request') {
            return;
          }

          // Empire's cart drawer listens for this and opens itself
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data },
          }));
        }.bind(this))
        .catch(function () {
          this.adding = false;
          btn.classList.remove('is-loading');
        }.bind(this));
    },
  };

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-cv2]').forEach(function (root) {
      var instance = new CollectionV2(root);

      // Restore the desktop sidebar preference
      try {
        if (sessionStorage.getItem('cv2SidebarHidden') === 'true') {
          root.classList.add('is-sidebar-hidden');
          var toggleBtn = root.querySelector('[data-cv2-filters-toggle]');
          if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
        }
      } catch (e) { /* storage unavailable */ }

      return instance;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
